import { apiMerchant } from "@/api"
import {
  clearStoredCompany,
  getCachedCompanyForSubdomain,
  persistCompanyIfValid,
  type Company,
} from "@/lib/company-storage"
import { isAxiosError } from "axios"

/**
 * Resolves the storefront subdomain the same way App bootstrap does.
 */
export function resolveStorefrontSubdomain(
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  devMode = import.meta.env.VITE_DEV_MODE,
): string {
  return devMode === "developer" ? "developer" : hostname.split(".")[0] ?? ""
}

export type EnsureCompanyResult = {
  company: Company | null
  healed: boolean
  error?: unknown
}

/**
 * Returns a valid company for the current host, healing localStorage when needed.
 *
 * - Cache matching the subdomain (top-level or domains.subdomain) → reuse.
 * - Missing cache → GET /by-subdomain/:subdomain and persist with stamped subdomain.
 * - Never clears a matching cache before the network call (avoids wipe-then-timeout).
 * - Timeout / 5xx with matching cache → keep cache.
 * - Invalid success body with matching cache → keep cache.
 * - 404 → clear storage and return null.
 */
export async function ensureCompanyForCurrentHost(
  options: {
    forceRefresh?: boolean
    subdomain?: string
  } = {},
): Promise<EnsureCompanyResult> {
  const subdomain = options.subdomain ?? resolveStorefrontSubdomain()
  if (!subdomain) {
    return { company: null, healed: false }
  }

  const cached = getCachedCompanyForSubdomain(subdomain)
  if (cached && !options.forceRefresh) {
    return { company: cached, healed: false }
  }

  try {
    const { data } = await apiMerchant.get(`/by-subdomain/${subdomain}`)
    const persisted = persistCompanyIfValid(data, subdomain)
    if (persisted) {
      return { company: persisted, healed: !cached || cached.id !== persisted.id }
    }
    if (cached) {
      return { company: cached, healed: false, error: data }
    }
    return { company: null, healed: false, error: data }
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      clearStoredCompany()
      return { company: null, healed: false, error }
    }
    if (cached) {
      return { company: cached, healed: false, error }
    }
    return { company: null, healed: false, error }
  }
}
